import React, { createContext, useContext, useEffect, useReducer } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useHistory, useParams } from 'react-router-dom';
import { deepRemoveKeys, noop } from '@moda/portal-stanchions';
import { ModalOverlay } from '@moda/om';
import { logger } from '../../lib/logger';
import { NULL_USER } from '../../lib/user';
import { reportError } from '../../lib/reportError';
import { getNetworkErrors, isGenericError } from '../../lib/error';
import { PaymentSource } from '../../constants';
import { hrefFor } from '../../routers';
import {
  CartUpdatePropertyName,
  CartUpdateAction,
  CartVersion,
  useUpdateCartMutation,
  GET_CART_QUERY,
  Cart,
  DEFAULT_CART,
  useCart
} from '../../hooks/useCart';
import { usePlaceOrderMutation } from '../../hooks/useCheckout/usePlaceOrderMutation';
import { useChargeOrder } from '../../hooks/useChargeOrder';
import { useErrorModal } from '../../hooks/useErrorModal';
import { useUser } from '../../hooks/useUser';
import { useOrderSummary } from '../../hooks/useOrderSummary';
import { OrderState } from '../../hooks/useUpdateOrder';
import { usePaymentError } from '../../hooks/useCheckout/usePaymentError';
import { useCheckoutErrorTracking } from '../../hooks/useCheckout/useCheckoutErrorTracking';
import { useCookies } from '../../hooks/useCookies';
import { Loading } from '../Loading';
import { CREATE_APPLE_PAY_MERCHANT_SESSION } from './createApplePayMerchantSession.rest';

const stringToNumStatus = (key: string) =>
  (ApplePaySession as unknown as { [key: string]: number })[key];

type State = {
  cart: Cart;
  contact: ApplePayJS.ApplePayPaymentContact | null;
};

const INITIAL_STATE = {
  cart: DEFAULT_CART,
  contact: null
};

type Action =
  | { type: 'UPDATE_CART'; payload: Cart }
  | { type: 'UPDATE_CONTACT'; payload: ApplePayJS.ApplePayPaymentContact }
  | { type: 'RESET' };

const ApplePayCartContext = createContext<{
  state: State;
  dispatch: (action: Action) => void;
}>({
  state: INITIAL_STATE,
  dispatch: noop
});

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'UPDATE_CART':
      return {
        ...state,
        cart: action.payload
      };

    case 'UPDATE_CONTACT':
      return {
        ...state,
        contact: action.payload
      };

    case 'RESET':
      return INITIAL_STATE;

    default:
      return state;
  }
};

const ApplePayCartProvider: React.FC<{ children?: React.ReactNode; cart: Cart }> = ({
  children,
  cart
}) => {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  useEffect(() => {
    if (cart) dispatch({ type: 'UPDATE_CART', payload: cart });
  }, [cart]);

  return (
    <ApplePayCartContext.Provider value={{ state, dispatch }}>
      {children}
    </ApplePayCartContext.Provider>
  );
};

const OnValidateMerchant = ({ session }: { session: ApplePaySession }) => {
  const [onCreateApplePayMerchantSessionHandler] = useMutation(CREATE_APPLE_PAY_MERCHANT_SESSION);
  const { showErrorModal } = useErrorModal();
  useEffect(() => {
    session.onvalidatemerchant = async event => {
      try {
        const { data } = await onCreateApplePayMerchantSessionHandler({
          variables: {
            input: {
              url: event.validationURL
            }
          }
        });

        session.completeMerchantValidation(data?.applePayMerchantSession);
      } catch (error) {
        reportError('APPLE_PAY_VALIDATE_MERCHANT', error);
        showErrorModal();
      }
    };
  }, [onCreateApplePayMerchantSessionHandler, session, showErrorModal]);

  return null;
};

const OnShippingContactSelected = ({ session }: { session: ApplePaySession }) => {
  const { state, dispatch } = useContext(ApplePayCartContext);
  const [updateCart] = useUpdateCartMutation(state.cart);
  const { showErrorModal } = useErrorModal();

  useEffect(() => {
    session.onshippingcontactselected = async event => {
      const result = await updateCart(
        CartUpdateAction.UpdateCart,
        {
          [CartUpdatePropertyName.RenderedBy]: CartVersion.Checkout,
          [CartUpdatePropertyName.PaymentSource]: PaymentSource.Apple,
          address: {
            [CartUpdatePropertyName.State]: event.shippingContact.administrativeArea?.toUpperCase(),
            [CartUpdatePropertyName.City]: event.shippingContact.locality,
            [CartUpdatePropertyName.Phone]: '',
            [CartUpdatePropertyName.PostalCode]: event.shippingContact.postalCode,
            [CartUpdatePropertyName.CountryCode]: event.shippingContact.countryCode?.toUpperCase(),
            [CartUpdatePropertyName.AddressLine1]: '',
            [CartUpdatePropertyName.AddressLine2]: '',
            [CartUpdatePropertyName.FirstName]: '',
            [CartUpdatePropertyName.LastName]: '',
            [CartUpdatePropertyName.Default]: false
          }
        },
        true
      );

      dispatch({ type: 'UPDATE_CONTACT', payload: event.shippingContact });

      const newCart = result.data?.updateCart.data[0];
      if (newCart) dispatch({ type: 'UPDATE_CART', payload: newCart });
      const newApplePaySummary = deepRemoveKeys(
        newCart?.attributes.applePaymentSummary,
        '__typename'
      );

      if (newApplePaySummary && Number(newApplePaySummary.data.total.amount) > 0) {
        session.completeShippingContactSelection(
          stringToNumStatus(newApplePaySummary.data.status.code),
          newApplePaySummary?.data.shippingMethods,
          newApplePaySummary?.data.total,
          newApplePaySummary?.data.lineItems
        );
      } else {
        showErrorModal();
        session.abort();
      }
    };
  }, [dispatch, session, showErrorModal, state.cart.attributes.currency, updateCart]);

  return null;
};

const OnShippingMethodSelected = ({ session }: { session: ApplePaySession }) => {
  const { showErrorModal } = useErrorModal();
  const { state, dispatch } = useContext(ApplePayCartContext);
  const [updateCart] = useUpdateCartMutation(state.cart);
  useEffect(() => {
    session.onshippingmethodselected = async event => {
      try {
        const result = await updateCart(
          CartUpdateAction.UpdateCart,
          {
            [CartUpdatePropertyName.RenderedBy]: CartVersion.Checkout,
            [CartUpdatePropertyName.PaymentSource]: PaymentSource.Apple,
            address: {
              [CartUpdatePropertyName.State]: state.contact?.administrativeArea?.toUpperCase(),
              [CartUpdatePropertyName.City]: state.contact?.locality,
              [CartUpdatePropertyName.Phone]: '',
              [CartUpdatePropertyName.PostalCode]: state.contact?.postalCode,
              [CartUpdatePropertyName.CountryCode]: state.contact?.countryCode?.toUpperCase(),
              [CartUpdatePropertyName.AddressLine1]: '',
              [CartUpdatePropertyName.AddressLine2]: '',
              [CartUpdatePropertyName.FirstName]: '',
              [CartUpdatePropertyName.LastName]: '',
              [CartUpdatePropertyName.Default]: false
            },
            [CartUpdatePropertyName.ShippingMethod]: parseInt(
              event.shippingMethod.identifier || '',
              10
            )
          },
          true
        );

        const newCart = result.data?.updateCart.data[0];
        if (newCart) dispatch({ type: 'UPDATE_CART', payload: newCart });
        const newApplePaySummary = deepRemoveKeys(
          newCart?.attributes.applePaymentSummary,
          '__typename'
        );

        if (newApplePaySummary && Number(newApplePaySummary.data.total.amount) > 0) {
          logger.info('APPLE_PAY_SHIPPING_SELECTED', event.shippingMethod.identifier, {
            shippingMethodId: event.shippingMethod.identifier
          });
          session.completeShippingMethodSelection(
            stringToNumStatus(newApplePaySummary.data.status.code),
            newApplePaySummary.data.total,
            newApplePaySummary.data.lineItems
          );
        } else {
          showErrorModal();
          session.abort();
        }
      } catch (error) {
        reportError('APPLE_PAY_SHIPPING_SELECTED', error);
      }
    };
  }, [
    dispatch,
    session,
    showErrorModal,
    state.cart.attributes.country2AlphaCode,
    state.cart.attributes.currency,
    state.contact?.administrativeArea,
    state.contact?.countryCode,
    state.contact?.locality,
    state.contact?.postalCode,
    updateCart
  ]);

  return null;
};

interface Props {
  session: ApplePaySession;
}

const OnPaymentAuthorized: React.FC<Props> = ({ session }) => {
  const history = useHistory();
  const [placeOrder] = usePlaceOrderMutation();
  const {
    state: {
      cart: {
        id: cartId,
        attributes: { countryCode, applePaymentSummary, shippingMethods, selectedShippingMethodId }
      }
    }
  } = useContext(ApplePayCartContext);

  const {
    setUserData,
    user: { isLoggedIn, firstName, email }
  } = useUser();
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const { orderSummary } = useOrderSummary(orderNumber);
  const { chargeOrder } = useChargeOrder(orderNumber);
  const { setPaymentError } = usePaymentError();
  const { showErrorModal } = useErrorModal();
  const { trackCheckoutError } = useCheckoutErrorTracking();

  useEffect(() => {
    const selectedShippingMethodIdentifier =
      applePaymentSummary?.data.shippingMethods &&
      applePaymentSummary?.data.shippingMethods.find(
        method => `${method.identifier}` === `${selectedShippingMethodId}`
      )?.identifier;
    session.onpaymentauthorized = async event => {
      const paymentDetails = {
        card_type: event.payment.token.paymentMethod.network,
        last_four: event.payment.token.paymentMethod.displayName.replace(/[^\d.]/g, ''),
        source: PaymentSource.Apple
      };

      const billingAddress = {
        billing_address: {
          city: event.payment.billingContact?.locality,
          state: event.payment.billingContact?.administrativeArea?.toUpperCase(),
          postal_code: event.payment.billingContact?.postalCode,
          country_code: event.payment.billingContact?.countryCode?.toUpperCase(),
          address_line1: event.payment.billingContact?.addressLines?.[0],
          address_line2: event.payment.billingContact?.addressLines?.[1],
          first_name: event.payment.billingContact?.givenName,
          last_name: event.payment.billingContact?.familyName
        }
      };

      const shippingAddress = {
        city: event.payment.shippingContact?.locality,
        state: event.payment.shippingContact?.administrativeArea?.toUpperCase(),
        phone: '',
        postal_code: event.payment.shippingContact?.postalCode,
        country_code: event.payment.shippingContact?.countryCode?.toUpperCase(),
        address_line1: event.payment.shippingContact?.addressLines?.[0],
        address_line2: event.payment.shippingContact?.addressLines?.[1],
        first_name:
          event.payment.shippingContact?.givenName ||
          firstName ||
          event.payment.shippingContact?.emailAddress?.split('@')?.[0],
        last_name: event.payment.shippingContact?.familyName,
        email: event.payment.shippingContact?.emailAddress,
        default: false
      };

      const applePayCart = {
        is_guest: !isLoggedIn,
        country_code: countryCode,
        cart_id: cartId || '',
        address: shippingAddress,
        payment_source: PaymentSource.Apple,
        payment_token: btoa(JSON.stringify(event.payment.token.paymentData)),
        other_payment_details: paymentDetails,
        payment_summary: billingAddress,
        ...(applePaymentSummary?.data.shippingMethods && {
          shipment_method: selectedShippingMethodIdentifier
        })
      };

      const applePaySecondPaymentOrder = {
        id: orderSummary?.id,
        payment_source: PaymentSource.Apple,
        payment_id: orderSummary?.attributes.selectedPaymentId,
        selected_items_ids: orderSummary?.attributes.selectedItemsIds,
        country_code: countryCode,
        use_site_credit: orderSummary?.attributes.useSiteCredit,
        use_reward_points: orderSummary?.attributes.useMoRewards,
        order_state: OrderState.Charge,
        payment_token: btoa(JSON.stringify(event.payment.token.paymentData)),
        other_payment_details: paymentDetails,
        payment_summary: billingAddress
      };

      if (!orderNumber) {
        try {
          const { data } = await placeOrder(applePayCart);
          const orderData = data?.checkout.data[0].attributes;

          if (orderData && orderData.guestAccessToken) {
            setUserData({
              ...NULL_USER,
              email: orderData.userEmail || event.payment.shippingContact?.emailAddress || '',
              accessToken: orderData.guestAccessToken,
              isMember: orderData.userIsMember || false,
              remember: true
            });
          }

          session.completePayment(ApplePaySession.STATUS_SUCCESS);

          if (orderData)
            history.push(
              hrefFor.OrderConfirmationPage({
                orderNumber: orderData.orderNumber
              })
            );
        } catch (error) {
          session.completePayment(ApplePaySession.STATUS_FAILURE);
          if (isGenericError(error) && error.reason !== 'canceled') {
            const networkErrorMessage = getNetworkErrors(error)?.[0]?.detail;
            const message =
              networkErrorMessage ||
              error.displayMessage ||
              'An error occurred. Please retry later.';
            reportError('APPLE_PAY_COMPLETE_PAYMENT', error);
            setPaymentError(message ?? null);
            showErrorModal(message);
            logger.info('PAYMENT_METHOD_FAILED', error.toString(), {
              error: error.toString(),
              networkErrorMessage,
              message,
              email: email,
              guest: !isLoggedIn
            });
            trackCheckoutError(PaymentSource.Apple, error);
          }
        }
      } else {
        const response = await chargeOrder(applePaySecondPaymentOrder);

        const orderData = response && response.data?.chargeOrder.data[0].attributes;
        const errorMessage = response && response.data?.chargeOrder.data[0].attributes.message;
        const errors = response && response.data?.chargeOrder.data[0].attributes.errors;
        const hasError =
          response &&
          response.data?.chargeOrder.data[0].attributes.errors &&
          response.data?.chargeOrder.data[0].attributes.errors.length > 0;

        if (errorMessage && hasError) {
          session.completePayment(ApplePaySession.STATUS_FAILURE);
          logger.info('SECOND_PAYMENT_FAILED', errorMessage, {
            error: errorMessage,
            email: email,
            guest: !isLoggedIn,
            paymentSource: orderSummary?.attributes.paymentMethod.source,
            payToday: orderSummary?.attributes.paymentSummary.payToday.price,
            total: orderSummary?.attributes.paymentSummary.total.price
          });
          setPaymentError(errorMessage ?? null);
          showErrorModal(errorMessage);
          const error = errors?.[0];
          trackCheckoutError(
            PaymentSource.Apple,
            error && typeof error === 'object' ? error.detail : error
          );
        }

        if (!hasError && orderData) {
          session.completePayment(ApplePaySession.STATUS_SUCCESS);
          logger.info('APPLE_PAY_COMPLETE_PAYMENT_SUCCESS', orderData.userEmail, {
            email: email,
            guest: !isLoggedIn,
            shippingMethod: selectedShippingMethodIdentifier,
            paymentSource: orderSummary?.attributes.paymentMethod.source,
            payToday: orderSummary?.attributes.paymentSummary.payToday.price,
            total: orderSummary?.attributes.paymentSummary.total.price
          });
          history.push(
            hrefFor.OrderDetailPage({
              orderNumber: orderData.orderNumber
            })
          );
        }
      }
    };
  }, [
    applePaymentSummary,
    chargeOrder,
    countryCode,
    firstName,
    email,
    history,
    isLoggedIn,
    orderNumber,
    orderSummary,
    placeOrder,
    session,
    setUserData,
    setPaymentError,
    showErrorModal,
    trackCheckoutError,
    cartId,
    shippingMethods,
    selectedShippingMethodId
  ]);

  return null;
};

export const SetupApplePaySession: React.FC<Props> = ({ session }) => {
  const { orderNumber } = useParams<{ orderNumber: string }>();

  const {
    cookies: { cartId }
  } = useCookies();

  const {
    cart: {
      attributes: { country2AlphaCode, countryCode }
    }
  } = useCart();

  const addressBasedCountryCode = country2AlphaCode || countryCode;
  const { data, loading, error } = useQuery(GET_CART_QUERY, {
    variables: {
      cartId: cartId || '',
      cartVersion: CartVersion.Checkout,
      countryCode: addressBasedCountryCode
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: !cartId,
    onCompleted: () => {
      session.begin();
    }
  });

  const cart = data?.userCart.data[0];

  if (error) throw error;

  if (loading)
    return (
      <ModalOverlay onClose={noop} show>
        <Loading />
      </ModalOverlay>
    );

  if (!cart) return null;

  return (
    <>
      <ApplePayCartProvider cart={cart}>
        <OnValidateMerchant session={session} />

        {!orderNumber && (
          <>
            <OnShippingContactSelected session={session} />

            <OnShippingMethodSelected session={session} />
          </>
        )}

        <OnPaymentAuthorized session={session} />
      </ApplePayCartProvider>
    </>
  );
};

export { OnValidateMerchant as ApplePayOnValidateMerchant };
